import React, { useEffect, useState } from 'react';
import { CROP_SIZES } from 'domain/shared/artifact';
import { useSelector } from 'react-redux';
import useAuthenticatedUserService, {
  UserName,
  AuthenticatedUserState,
} from 'infrastructure/security/authentication/authenticatedUserService';
import Avatar from '@mui/material/Avatar';
import { Box } from '@mui/material';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';

const AVATAR_SIZE = { width: { xs: 35, sm: 40 }, height: { xs: 35, sm: 40 } };
const STATUS_BADGE_SX = {
  position: 'absolute',
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 14,
  height: 14,
  borderRadius: '50%',
  backgroundColor: 'var(--mui-palette-background-paper)',
  border: '1px solid var(--mui-palette-background-paper)',
} as const;

const STATUS_BADGE_ICON_SX = {
  width: 10,
  height: 10,
} as const;

const getFirstLetter = (word: string) => word.toUpperCase()[0];

const getInitials = (name: UserName) => `${getFirstLetter(name.firstName)}${getFirstLetter(name.lastName)}`;

type CurrentUserAvatarFieldProps = Readonly<{
  statusIcon?: string;
}>;

const CurrentUserAvatarField = ({ statusIcon }: CurrentUserAvatarFieldProps) => {
  const { isFeatureEnabled } = useFeatureToggle();
  const [name, setName] = useState<Nullable<UserName>>(null);
  const [avatarThumbnail, setAvatarThumbnail] = useState<Nullable<string>>(null);
  const { getUserName, getAvatarUrl } = useAuthenticatedUserService();
  const user = useSelector((state: { auth: AuthenticatedUserState }) => state?.auth?.user);

  useEffect(() => {
    (async () => {
      const userName = await getUserName();
      const thumbnail = await getAvatarUrl(CROP_SIZES.SIZE_100);
      setName(userName);
      setAvatarThumbnail(thumbnail);
    })();
  }, [user?.avatarId, getUserName, getAvatarUrl]);

  if (!name) {
    return null;
  }

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      {avatarThumbnail ? (
        <Avatar sx={AVATAR_SIZE} src={avatarThumbnail} alt={name.firstName} />
      ) : (
        <Avatar sx={AVATAR_SIZE}>{getInitials(name)}</Avatar>
      )}
      {statusIcon && isFeatureEnabled(Features.INTERRUPTIONS) ? (
        <Box sx={STATUS_BADGE_SX}>
          <Box component="img" src={statusIcon} alt="" aria-hidden="true" sx={STATUS_BADGE_ICON_SX} />
        </Box>
      ) : null}
    </Box>
  );
};

export default CurrentUserAvatarField;
